import React from 'react';
import { Block } from 'slate'

import {
  NODE_TYPE as NT,
  RENDER_MODE,
  HEADLINE_CHAR_LIMIT,
} from 'shared/constants/slate';
import {
  BASE_COLOR as BC,
  MOA_COLOR as MC,
} from 'shared/constants/colors';
import { noop } from 'shared/helpers';
import { classes as cn } from 'shared/styles';

import { cx, css } from 'helpers';
import { FontIcon } from 'components/elems';

const { RENDER_FOR_EDIT, RENDER_FOR_PREVIEW } = RENDER_MODE;



const AddMoreBtn = ({ className, style = {}, ...props }) => (
  <button
    className={cx(
      className,
      '-typography-base',
      '-button-reset',
      'flex silver f5 hover-dark-gray',
      cn.animateFadeInSlow,
      // cn.colorDarkGrayOnHover,
    )}
    style={{
      transition: 'color 200ms',
      ...style,
    }}
    {...props}
  >
    <FontIcon type="enter" />
    <span className="ml1">Add more</span>
  </button>
)



const LeftIndicator = ({
  isVisible = false,
  label = 'Title',
}) => (
  <div
    contentEditable={false}
    spellCheck={false}
    className={cx(
      "absolute f6 fw3 tr pr4 moon-gray dn db-l",
      {'o-0': !isVisible},
    )}
    style={{
      top: '8px',
      left: '-96px',
      width: '96px',
      transition: 'opacity 200ms',
      userSelect: 'none',
    }}
  >
    {label}
  </div>
);


const BottomIndicator = (props) => {
  const {
    editor,
    headlineHasFocus,
    node: headline,
  } = props;

  const charsTypedSoFarCount = headline.text.length;
  const charsRemainingCount = HEADLINE_CHAR_LIMIT - charsTypedSoFarCount;
  const isRunningOutOfChars = charsRemainingCount <= 20;
  const isOutOfChars = charsTypedSoFarCount > HEADLINE_CHAR_LIMIT;

  const { document: doc } = editor.props.value;
  const headlineIsEmpty = headline.text.trim().length === 0;
  const isHeadless = doc.nodes.size > 1 && headlineIsEmpty;

  const isVisible = (
    (headlineHasFocus && !headline.isEmpty)
    || isOutOfChars
    || isHeadless
  );

  return (
    <div
      contentEditable={false}
      style={{
        // transition: 'opacity 200ms',
        userSelect: 'none',
      }}
      className={cx(
        "flex items-center justify-end mt2",
        {[cn.animateFadeInSlow]: isVisible},
        {'o-0': !isVisible},
      )}
    >
      <span
        className={cx(
          "f7 moon-gray tr fw2",
          {[runningOutOfCharsColor]: isRunningOutOfChars && !isOutOfChars},
          {[isOutOfCharsColor]: isOutOfChars || isHeadless},
        )}
        style={{
          transition: 'color 200ms',
          minWidth: '88px',
        }}
      >
        {charsTypedSoFarCount} / {HEADLINE_CHAR_LIMIT}
      </span>
    </div>
  );
};

const runningOutOfCharsColor = css({ color: BC.darkGray });
const isOutOfCharsColor      = css({ color: MC.stories.brinkPink });



const handleClickOnAddMoreBtn = (props) => () => {
  const {
    editor,
    hideToolbarRow = noop,
  } = props;
  const { document: doc } = editor.value;
  const paragraph = Block.create(NT.paragraph);

  editor.change(change => {
    change
      .focus()
      .insertNodeByKey(
        doc.key,
        doc.nodes.size,
        paragraph
      )
      .collapseToStartOfNextBlock();
  });

  // Slate seems to be diff-ing node value and skip
  // rendering if not changed. When clicking on it
  // in a blurred state, headline doesn't re-render so
  // just manually hide toolbarRow.
  hideToolbarRow();
};

const HeadlineToolbarRow = (props) => {
  const { node: headline } = props;
  const headlineIsEmpty = headline.text.trim().length === 0;

  return (
    <div
      contentEditable={false}
      className="flex justify-start"
      style={{
        userSelect: 'none',
        marginTop: '1.5rem',
        minHeight: '30px',  // Make it as tall as <p>?
      }}
    >
      {headlineIsEmpty ? (
        <div />
      ) : (
        <AddMoreBtn onClick={handleClickOnAddMoreBtn(props)} />
      )}

      {/*
        XXX: So, this got to be the craziest, most insane bug with
        React/Slate that I'm hopeless. Basically if staging content
        by clicking this button instead of from NavbarPortal, then
        Textarea in StageContentPanel gets fucked up by losing focus
        on every key stroke, but only happens w/ Chrome, not Firefox.

        If clicking the same exact button through NavbarPortal, then
        it's not an issue. Something about this button being inside
        Slate Editor, and maybe keeping a focus on the main editor
        when clicked, and/or having another readOnly editor for Preview..

        It's fucking weird, ain't nobody got time for this,
        so fuck this button.

        <StageContentBtn
          onClick={stageContent}
          isDisabled={headlineIsEmpty || headlineIsOverLimit}
        />
      */}
    </div>
  )
};



/*
 * Headline
 */
class Headline extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasToolbarRow: false };
  }

  componentWillReceiveProps(nextProps) {
    const { hasToolbarRow } = nextProps;
    hasToolbarRow ? this.showToolbarRow() : this.hideToolbarRow();
  }

  showToolbarRow = () => {
    this.setState({ hasToolbarRow: true });
  }

  hideToolbarRow = () => {
    // XXX(thx): I know using forceUpdate is a no-no but
    // normal setState to false never fires for some reason,
    // and I can'figure out why and YOLO, ffs. ¯\_(ツ)_/¯
    this.forceUpdate(() => {
      this.setState({ hasToolbarRow: false });
    });
  }

  render() {
    const { editor, node: headline, ...rest } = this.props;
    const props = { editor, node: headline, ...rest };

    const { selection: sel } = editor.props.value;
    const headlineHasFocus = (
      sel.isFocused
      && sel.hasStartIn(headline)
      && sel.hasEndIn(headline)
    );

    return (
      <h1
        className={cx(
          headlineClass,
          'relative',  // For LeftIndicator absolute pos and height.
          {'-headline-has-focus': headlineHasFocus},
          '-headline-base',
        )}
        style={{
          lineHeight: 1.375,   // Between lh-title (1.25) and lh-copy (1.5)
        }}
        {...props.attributes}
      >
        {props.children}

        <LeftIndicator
          isVisible={headlineHasFocus}
          label="Lede"
        />

        <BottomIndicator
          {...props}
          headlineHasFocus={headlineHasFocus}
        />

        {this.state.hasToolbarRow && (
          <HeadlineToolbarRow
            {...props}
            hideToolbarRow={this.hideToolbarRow}
          />
        )}
      </h1>
    );
  }
}

const headlineClass = css({
  '& > span': {
    position: 'relative',
  },

  '& > span:before': {
    content: '""',
    position: 'absolute',
    transition: 'opacity 200ms',
    borderLeft: `1px solid ${BC.moonGray}`,
    top: 0,
    left: '-16px',
    // XXX: Doesn't stretch fully in Firefox
    height: '100%',
    opacity: 0,
  },

  '&.-headline-has-focus > span:before': {
    opacity: 1,
  },
});


/*
 * HeadlinePreview
 */
const HeadlinePreview = (props) => (
  <h1
    className={cx('-headline-base')}
    {...props.attributes}
  >
    {props.children}
  </h1>
);



/*
 * HeadlineSwitch
 */
const HeadlineSwitch = ({renderMode = RENDER_FOR_EDIT, ...props}) => {
  switch (renderMode) {
    case RENDER_FOR_EDIT: return <Headline {...props} />
    case RENDER_FOR_PREVIEW: return <HeadlinePreview {...props} />;
    default: return null;
  }
};


export {
  HeadlineSwitch,
}



// WEBPACK FOOTER //
// ./src/components/slate/nodes/headline.js